<template>
    <skeleton-loader v-if="loading" type="stats-panel" />

    <div v-else class="aud-panel">
        <div class="aud-kpis-wide">
            <kpi-card size="lg" :label="$t('business.ltv')" :value="ltvLabel" :hint="$t('business.ltv_hint')" />
            <kpi-card size="lg" :label="$t('business.monthly_ticket')" :value="ticketLabel"
                :hint="$t('business.monthly_ticket_hint')" />
        </div>

        <div class="aud-kpis">
            <kpi-card v-for="band in bands" :key="band.key" :label="$t(`business.${band.key}`)"
                :value="band.value" :hint="$t(`business.${band.key}_hint`)" />
            <kpi-card :label="$t('business.avg_seniority')" :value="seniorityLabel"
                :hint="$t('business.avg_seniority_hint')" />
        </div>

        <div class="aud-body">
            <div class="aud-grid">
                <stat-card>
                    <template v-slot:header>
                        <div class="aud-toggle">
                            <button type="button" class="aud-toggle-btn" :class="{ 'aud-toggle-btn--on': !byAge }"
                                @click="byAge = false">
                                {{ $t('business.gender') }}
                            </button>
                            <button type="button" class="aud-toggle-btn" :class="{ 'aud-toggle-btn--on': byAge }"
                                @click="byAge = true">
                                {{ $t('business.age') }}
                            </button>
                        </div>
                    </template>
                    <horizontal-bars v-if="byAge" :items="ageItems" show-percent
                        :empty-text="$t('business.no_audience_data')" />
                    <donut-breakdown v-else :items="genderItems" :size="92"
                        :empty-text="$t('business.no_audience_data')" />
                </stat-card>

                <stat-card>
                    <template v-slot:header>
                        <span class="chart-title">{{ $t('business.origin') }}</span>
                    </template>
                    <donut-breakdown :items="originItems" :size="92" :empty-text="$t('business.no_audience_data')" />
                </stat-card>

                <stat-card>
                    <template v-slot:header>
                        <span class="chart-title">{{ $t('business.sport') }}</span>
                    </template>
                    <horizontal-bars :items="sportItems" show-percent
                        :empty-text="$t('business.no_audience_data')" />
                </stat-card>

                <stat-card>
                    <template v-slot:header>
                        <span class="chart-title">{{ $t('business.area') }}</span>
                    </template>
                    <horizontal-bars :items="areaItems" show-percent
                        :empty-text="$t('business.no_audience_data')" />
                </stat-card>
            </div>

            <stat-card class="aud-cohort">
                <template v-slot:header>
                    <span class="chart-title">{{ $t('business.cohort_retention') }}</span>
                </template>
                <span class="aud-cohort-hint">{{ $t('business.cohort_hint') }}</span>
                <cohort-table v-if="hasCohorts" :rows="cohorts" />
                <div v-else class="aud-empty">{{ $t('business.no_cohort_data') }}</div>
            </stat-card>
        </div>
    </div>
</template>

<script>
import SkeletonLoader from "@/components/common/SkeletonLoader";
import KpiCard from "@/components/common/KpiCard";
import StatCard from "@/components/statistics/StatCard";
import DonutBreakdown from "@/components/common/DonutBreakdown";
import HorizontalBars from "@/components/common/HorizontalBars";
import CohortTable from "@/components/company/business/CohortTable";
import {
    average, buildCohorts, clientLifespan, formatMoney, isActiveAt, loadRoster, mergeRoster,
    periodReference, topCounts,
} from "@/utils/Business";

const GENDER_COLORS = { M: '#66CCCC', F: '#CCFF33', O: '#9B6DFF' }

const ORIGIN_COLORS = ['#E85C5C', '#FF8C42', '#66CC99', '#339999', '#9B6DFF']

const SPORT_COLORS = ['#66CCCC', '#66CC99', '#339999', '#99CC44', '#2A8080']

const AREA_COLORS = ['#E85C5C', '#FF8C42', '#66CCCC', '#66CC99', '#9B6DFF', '#E6AC00', '#4FA8D8']

const BANDS = [
    { key: 'seniority_0_3', min: 0, max: 3 },
    { key: 'seniority_4_6', min: 4, max: 6 },
    { key: 'seniority_7', min: 7, max: null },
]

const AGE_COLORS = ['#66CCCC', '#66CC99', '#339999', '#99CC44', '#2A8080', '#9B6DFF', '#E6AC00']

const AGE_BANDS = (() => {
    const bands = []
    for (let from = 5; from <= 60; from += 5) bands.push({ from, to: from + 4 })
    bands.push({ from: 65, to: null })
    return bands
})()

export default {
    name: "StatsAudience",
    components: { SkeletonLoader, KpiCard, StatCard, DonutBreakdown, HorizontalBars, CohortTable },
    props: {
        companyId: {
            type: String,
            default: null,
        },
        period: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            loading: true,
            clients: [],
            students: [],
            byAge: false,
        }
    },
    computed: {
        roster(vm = this) {
            return mergeRoster(vm.clients, vm.students)
        },
        reference(vm = this) {
            return periodReference(vm.period)
        },
        active(vm = this) {
            return vm.roster.filter(client => isActiveAt(client, vm.reference))
        },
        withPlan(vm = this) {
            return vm.active.filter(client => (Number(client.planAmount) || 0) > 0)
        },
        ticket(vm = this) {
            return average(vm.withPlan.map(client => Number(client.planAmount) || 0))
        },
        ticketLabel(vm = this) {
            return vm.ticket === null ? null : formatMoney(vm.ticket)
        },
        ltv(vm = this) {
            return average(vm.withPlan
                .filter(client => clientLifespan(client, vm.reference) !== null)
                .map(client => Number(client.planAmount) * clientLifespan(client, vm.reference)))
        },
        ltvLabel(vm = this) {
            return vm.ltv === null ? null : formatMoney(vm.ltv)
        },
        bands(vm = this) {
            const lifespans = vm.active
                .map(client => clientLifespan(client, vm.reference))
                .filter(months => months !== null)

            return BANDS.map(band => {
                const count = lifespans.filter(months => {
                    return months >= band.min && (band.max === null || months <= band.max)
                }).length
                return {
                    key: band.key,
                    value: lifespans.length ? `${Math.round(count / lifespans.length * 100)}%` : null,
                }
            })
        },
        seniority(vm = this) {
            return average(vm.roster
                .map(client => clientLifespan(client, vm.reference))
                .filter(months => months !== null))
        },
        seniorityLabel(vm = this) {
            if (vm.seniority === null) return null
            return vm.$t('business.months_value', { n: vm.seniority.toFixed(1) })
        },
        genderItems(vm = this) {
            return topCounts(vm.roster.map(client => client.gender), 3)
                .map(item => ({
                    label: vm.$t(`business.gender_${vm.genderKey(item.label)}`),
                    value: item.value,
                    color: GENDER_COLORS[item.label] || GENDER_COLORS.O,
                }))
        },
        ageItems(vm = this) {
            const ages = vm.roster.map(client => Number(client.age)).filter(age => age >= 5)
            return AGE_BANDS
                .map((band, i) => ({
                    label: band.to ? `${band.from}-${band.to}` : `${band.from}+`,
                    value: ages.filter(age => age >= band.from && (!band.to || age <= band.to)).length,
                    color: AGE_COLORS[i % AGE_COLORS.length],
                }))
                .filter(item => item.value > 0)
        },
        originItems(vm = this) {
            return topCounts(vm.roster.map(client => client.origin), 5)
                .map((item, i) => ({
                    label: vm.$te(`business.origin_${item.label}`)
                        ? vm.$t(`business.origin_${item.label}`)
                        : item.label,
                    value: item.value,
                    color: ORIGIN_COLORS[i % ORIGIN_COLORS.length],
                }))
        },
        sportItems(vm = this) {
            const other = vm.$t('business.other_group')
            return topCounts(vm.roster.map(client => vm.sportLabel(client.sport)), 5, other)
                .map((item, i) => ({ ...item, color: SPORT_COLORS[i % SPORT_COLORS.length] }))
        },
        areaItems(vm = this) {
            const other = vm.$t('business.other_group')
            return topCounts(vm.roster.map(client => client.area), 7, other)
                .map((item, i) => ({ ...item, color: AREA_COLORS[i % AREA_COLORS.length] }))
        },
        cohorts(vm = this) {
            return buildCohorts(vm.roster, vm.period)
        },
        hasCohorts(vm = this) {
            return vm.cohorts.some(row => row.total > 0)
        },
    },
    mounted() {
        this.load()
    },
    methods: {
        genderKey(value) {
            return ['M', 'F'].includes(value) ? { M: 'male', F: 'female' }[value] : 'other'
        },
        async load() {
            const vm = this
            if (!vm.companyId) return
            vm.loading = true
            try {
                const { clients, students } = await loadRoster(vm.companyId)
                vm.clients = clients
                vm.students = students
            } catch (error) {
                console.error('[business] loadRoster', error?.code, error?.message)
            } finally {
                vm.loading = false
            }
        },
    },
}
</script>

<style scoped>
.aud-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.aud-kpis-wide {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

.aud-kpis {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
}

.aud-body {
    display: grid;
    grid-template-columns: 1.35fr 1fr;
    gap: 16px;
    align-items: start;
}

.aud-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.aud-grid > * {
    min-height: 148px;
}

.aud-cohort {
    max-height: 320px;
}

.aud-cohort-hint {
    font-size: 11px;
    line-height: 1.3;
    color: var(--v-txtMuted-base);
    margin-bottom: 8px;
}

.chart-title {
    font-size: var(--fs-caption);
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.aud-toggle {
    display: flex;
    gap: 2px;
    padding: 2px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
}

.aud-toggle-btn {
    height: 22px;
    padding: 0 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    transition: background 0.15s, color 0.15s;
}

.aud-toggle-btn--on {
    background: var(--v-surface-base);
    color: var(--v-txtPrimary-base);
}

.aud-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1 1 auto;
    min-height: 90px;
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

@media (max-width: 1264px) {
    .aud-body {
        grid-template-columns: 1fr;
    }

    .aud-cohort {
        max-height: none;
    }
}

@media (max-width: 960px) {
    .aud-kpis-wide,
    .aud-kpis {
        grid-template-columns: repeat(2, 1fr);
    }

    .aud-grid {
        grid-template-columns: 1fr;
    }
}
</style>
